import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Crown, Edit3, Trash2, X, Save, Loader2, Building2, Users as UsersIcon,
  Scissors, Calendar, Infinity as InfinityIcon, Power, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((it) => {
      if (typeof it === "string") return it;
      const where = Array.isArray(it?.loc) ? it.loc.slice(-1)[0] : null;
      return where ? `${where}: ${it.msg}` : it?.msg || JSON.stringify(it);
    }).join(" · ");
  }
  return typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d);
};

// Single-row chip for a limit, with the ∞ symbol when unlimited (null).
const LimitChip = ({ Icon, value, label, lang }) => (
  <div className="flex items-center gap-1.5 text-xs text-stone-600">
    <Icon size={12} className="text-stone-400" />
    <span className="num-display font-semibold text-stone-800">
      {value === null || value === undefined
        ? <InfinityIcon size={13} className="inline text-emerald-600"/>
        : value}
    </span>
    <span className="text-stone-400">{label}</span>
  </div>
);

// Editable "number OR unlimited" field — checkbox flips the underlying value
// between an integer and null. Kept inline because it's the unique UX twist
// of this screen.
const LimitField = ({ label, value, onChange, lang }) => {
  const isAr = lang === "ar";
  const unlimited = value === null || value === undefined;
  return (
    <div>
      <label className="text-xs text-stone-500 flex items-center justify-between">
        <span>{label}</span>
        <label className="inline-flex items-center gap-1 text-[10px] text-stone-500 cursor-pointer">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => onChange(e.target.checked ? null : 0)}
            data-testid={`limit-unlimited-${label}`}
          />
          {isAr ? "غير محدود" : "Unlimited"}
        </label>
      </label>
      <div className="relative mt-1">
        <input
          type="number"
          min="0"
          className="input-dark"
          value={unlimited ? "" : value ?? ""}
          disabled={unlimited}
          onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value, 10))}
          placeholder={unlimited ? "∞" : ""}
        />
        {unlimited && (
          <span className="absolute end-3 top-1/2 -translate-y-1/2 text-emerald-600 pointer-events-none">
            <InfinityIcon size={16}/>
          </span>
        )}
      </div>
    </div>
  );
};

const emptyForm = {
  code: "",
  name_en: "",
  name_ar: "",
  monthly_price: 0,
  max_branches: 1,
  max_users: 5,
  max_services: 50,
  max_bookings_per_month: 500,
  features: [],
  pages: [],
  description: "",
  active: true,
  sort_order: 100,
};

const PlanModal = ({ open, mode, initial, onClose, onSaved, lang }) => {
  const isAr = lang === "ar";
  const editing = mode === "edit";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [featureInput, setFeatureInput] = useState("");
  // Page catalog drives the access-control checkbox grid below the marketing
  // chips. Fetched once when the modal opens; cached for the lifetime of the
  // PlansAdmin screen via lifting state into the parent would be overkill —
  // this modal opens once per edit.
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptyForm, ...initial, pages: initial.pages || [] } : emptyForm);
      setFeatureInput("");
      if (!catalog) {
        api.get("/admin/plans/page-catalog")
          .then(r => setCatalog(r.data))
          .catch(() => setCatalog({ groups: [], pages: [], always_on: [] }));
      }
    }
  }, [open, initial]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const addFeature = () => {
    const v = featureInput.trim();
    if (!v) return;
    if (!form.features.includes(v)) {
      setForm({ ...form, features: [...form.features, v] });
    }
    setFeatureInput("");
  };
  const removeFeature = (v) => setForm({ ...form, features: form.features.filter(x => x !== v) });

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      // Strip `code` and immutable fields on PATCH so the backend doesn't
      // wedge the unique-index when the operator left them untouched.
      const payload = editing
        ? (() => {
            const { code, plan_id, tenants_count, created_at, updated_at, ...rest } = form;
            return rest;
          })()
        : form;
      if (editing) {
        await api.patch(`/admin/plans/${initial.plan_id}`, payload);
      } else {
        await api.post("/admin/plans", payload);
      }
      toast.success(isAr ? "تم الحفظ" : "Saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown} data-testid="plan-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl gold-text">
            {editing ? (isAr ? "تعديل الخطة" : "Edit Plan") : (isAr ? "خطة جديدة" : "New Plan")}
          </h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20}/></button>
        </div>

        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-stone-500">{isAr ? "الرمز (لا يتغير لاحقاً)" : "Code (immutable)"}</label>
            <input
              className="input-dark mt-1 font-mono"
              value={form.code}
              onChange={(e) => setForm({...form, code: e.target.value.toLowerCase()})}
              required
              disabled={editing}
              pattern="[a-z][a-z0-9_]{1,30}"
              placeholder="e.g. starter"
              data-testid="plan-code"
            />
          </div>
          <div>
            <label className="text-xs text-stone-500">{isAr ? "السعر شهرياً (ر.ق)" : "Monthly Price (QAR)"}</label>
            <input type="number" min="0" step="0.01" className="input-dark mt-1" value={form.monthly_price}
              onChange={(e) => setForm({...form, monthly_price: parseFloat(e.target.value) || 0})}
              data-testid="plan-price"/>
          </div>
          <div>
            <label className="text-xs text-stone-500">Name (EN) *</label>
            <input className="input-dark mt-1" value={form.name_en} required
              onChange={(e) => setForm({...form, name_en: e.target.value})} data-testid="plan-name-en"/>
          </div>
          <div>
            <label className="text-xs text-stone-500">Name (AR)</label>
            <input className="input-dark mt-1" value={form.name_ar}
              onChange={(e) => setForm({...form, name_ar: e.target.value})} data-testid="plan-name-ar"/>
          </div>

          <LimitField
            label={isAr ? "الحد الأقصى للفروع" : "Max Branches"}
            value={form.max_branches}
            onChange={(v) => setForm({...form, max_branches: v})} lang={lang}/>
          <LimitField
            label={isAr ? "الحد الأقصى للمستخدمين" : "Max Users"}
            value={form.max_users}
            onChange={(v) => setForm({...form, max_users: v})} lang={lang}/>
          <LimitField
            label={isAr ? "الحد الأقصى للخدمات" : "Max Services"}
            value={form.max_services}
            onChange={(v) => setForm({...form, max_services: v})} lang={lang}/>
          <LimitField
            label={isAr ? "الحد الأقصى للحجوزات/شهر" : "Max Bookings / Month"}
            value={form.max_bookings_per_month}
            onChange={(v) => setForm({...form, max_bookings_per_month: v})} lang={lang}/>

          <div className="col-span-2">
            <label className="text-xs text-stone-500 flex items-center justify-between">
              <span>{isAr ? "المزايا التسويقية (chips للعرض فقط)" : "Marketing Features (display only)"}</span>
              <span className="text-[10px] text-stone-400">{isAr ? "لا تؤثر على الوصول" : "Does not affect access"}</span>
            </label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-stone-200 rounded-lg bg-stone-50/50 mt-1 min-h-[44px]">
              {form.features.map(f => (
                <span key={f} className="inline-flex items-center gap-1 bg-[var(--gold-tint)] text-[var(--gold)] text-[11px] px-2 py-0.5 rounded-full">
                  {f}
                  <button type="button" onClick={() => removeFeature(f)} className="hover:text-rose-600"><X size={10}/></button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[120px] bg-transparent outline-none text-xs"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                placeholder={isAr ? "اكتب ميزة واضغط Enter" : "Type and press Enter"}
                data-testid="plan-feature-input"
              />
            </div>
          </div>

          {/* ============ Page access checkboxes (THE access control) ============ */}
          <div className="col-span-2" data-testid="plan-pages-section">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-stone-500">
                <span className="font-semibold text-stone-700">{isAr ? "الصفحات المتاحة للمستأجر" : "Pages enabled for tenants"}</span>
                <span className="block text-[10px] text-stone-400 mt-0.5">
                  {isAr
                    ? "حدد الصفحات التي يستطيع المستأجرون على هذه الخطة الوصول إليها — على مستوى الصفحة بدقة."
                    : "Tick the pages tenants on this plan can access — page-by-page precision."}
                </span>
              </label>
              <div className="text-[10px] num-display font-semibold text-amber-700">
                {(form.pages || []).length} / {catalog?.pages?.length || 0}
              </div>
            </div>

            {!catalog ? (
              <div className="p-4 text-center text-stone-400 text-xs flex items-center justify-center gap-2 border border-stone-100 rounded-xl">
                <Loader2 className="animate-spin" size={12}/>{isAr ? "تحميل القائمة..." : "Loading catalog..."}
              </div>
            ) : (
              <div className="space-y-3 border border-stone-100 rounded-xl p-3 bg-stone-50/40">
                {(catalog.groups || []).map(g => {
                  const pagesInGroup = (catalog.pages || []).filter(p => p.group === g.id);
                  if (!pagesInGroup.length) return null;
                  const allChecked = pagesInGroup.every(p => (form.pages || []).includes(p.id) || p.always_on);
                  const toggleGroup = () => {
                    const groupIds = pagesInGroup.filter(p => !p.always_on).map(p => p.id);
                    const cur = new Set(form.pages || []);
                    if (allChecked) groupIds.forEach(id => cur.delete(id));
                    else groupIds.forEach(id => cur.add(id));
                    setForm({ ...form, pages: Array.from(cur) });
                  };
                  return (
                    <div key={g.id} className="border border-stone-200 rounded-lg p-2.5 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-[11px] text-stone-700 uppercase tracking-wider">
                          {isAr ? g.name_ar : g.name_en}
                        </div>
                        <button
                          type="button"
                          onClick={toggleGroup}
                          className="text-[10px] text-amber-700 hover:underline"
                          data-testid={`plan-group-toggle-${g.id}`}
                        >
                          {allChecked ? (isAr ? "إلغاء الكل" : "Uncheck all") : (isAr ? "تحديد الكل" : "Check all")}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {pagesInGroup.map(p => {
                          const isOn = (form.pages || []).includes(p.id) || p.always_on;
                          return (
                            <label
                              key={p.id}
                              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] cursor-pointer border transition ${
                                isOn ? "border-emerald-300 bg-emerald-50/60 text-emerald-900" : "border-stone-200 bg-white text-stone-600 hover:border-amber-300"
                              } ${p.always_on ? "opacity-80 cursor-not-allowed" : ""}`}
                              data-testid={`plan-page-${p.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={isOn}
                                disabled={p.always_on}
                                onChange={(e) => {
                                  const cur = new Set(form.pages || []);
                                  if (e.target.checked) cur.add(p.id); else cur.delete(p.id);
                                  setForm({ ...form, pages: Array.from(cur) });
                                }}
                                className="w-3.5 h-3.5"
                              />
                              <span className="flex-1 truncate">{isAr ? p.name_ar : p.name_en}</span>
                              {p.always_on && <span title={isAr ? "مفعّل دائماً" : "Always on"} className="text-[9px] text-amber-700">🔒</span>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="col-span-2">
            <label className="text-xs text-stone-500">{isAr ? "الوصف" : "Description"}</label>
            <textarea rows={2} className="input-dark mt-1" value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}/>
          </div>

          <div>
            <label className="text-xs text-stone-500">{isAr ? "ترتيب العرض" : "Sort Order"}</label>
            <input type="number" className="input-dark mt-1" value={form.sort_order}
              onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value) || 0})}/>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-stone-200 hover:border-emerald-400 cursor-pointer w-full">
              <input type="checkbox" checked={form.active}
                onChange={(e) => setForm({...form, active: e.target.checked})} data-testid="plan-active"/>
              <Power size={14} className={form.active ? "text-emerald-600" : "text-stone-400"}/>
              <span className="text-sm">{form.active ? (isAr ? "نشطة" : "Active") : (isAr ? "معطّلة" : "Disabled")}</span>
            </label>
          </div>

          <div className="col-span-2 flex justify-end gap-2 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100">{isAr ? "إلغاء" : "Cancel"}</button>
            <button type="submit" disabled={saving} className="btn-gold px-5 py-2 rounded-lg flex items-center gap-2" data-testid="plan-save">
              {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
              {isAr ? "حفظ" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function PlansAdmin() {
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const isAr = lang === "ar";

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, mode: "create", initial: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.plans || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (plan) => {
    if (plan.tenants_count > 0) {
      toast.error(isAr
        ? `لا يمكن الحذف: ${plan.tenants_count} مستأجر على هذه الخطة. عطّلها بدلاً من الحذف.`
        : `Cannot delete: ${plan.tenants_count} tenants on this plan. Disable it instead.`);
      return;
    }
    const ok = await confirm({
      title: isAr ? `حذف خطة "${plan.name_en}" نهائياً؟` : `Delete plan "${plan.name_en}" permanently?`,
      confirmLabel: isAr ? "حذف" : "Delete",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/plans/${plan.plan_id}`);
      toast.success(isAr ? "تم الحذف" : "Deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onToggleActive = async (plan) => {
    try {
      await api.patch(`/admin/plans/${plan.plan_id}`, { active: !plan.active });
      toast.success(plan.active
        ? (isAr ? "تم التعطيل" : "Disabled")
        : (isAr ? "تم التفعيل" : "Activated"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-5" data-testid="plans-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl gold-text">{isAr ? "تعريف خطط الاشتراك" : "Subscription Plans"}</h1>
          <p className="text-sm text-stone-500 mt-1">
            {isAr
              ? "أنشئ، عدّل، أو عطّل خطط الاشتراك. كل القوائم المنسدلة في المنصة تقرأ من هنا."
              : "Create, edit or disable the subscription tiers. Every plan dropdown in the platform reads from this catalog."}
          </p>
        </div>
        <button
          onClick={() => setModal({ open: true, mode: "create", initial: null })}
          className="btn-gold px-5 py-2.5 rounded-xl flex items-center gap-2 shadow-md"
          data-testid="add-plan"
        >
          <Plus size={16}/>{isAr ? "خطة جديدة" : "New Plan"}
        </button>
      </div>

      {/* Plan cards grid */}
      {loading ? (
        <div className="p-10 text-center text-stone-400 text-sm flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={14}/>{isAr ? "جارٍ التحميل..." : "Loading..."}
        </div>
      ) : plans.length === 0 ? (
        <div className="p-10 text-center text-stone-400 text-sm">{isAr ? "لا توجد خطط بعد." : "No plans defined yet."}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map(p => (
            <div
              key={p.plan_id}
              className={`bg-white rounded-2xl border shadow-sm p-5 relative transition ${
                p.active ? "border-stone-100" : "border-stone-200 bg-stone-50/40 opacity-90"
              }`}
              data-testid={`plan-card-${p.code}`}
            >
              {!p.active && (
                <span className="absolute top-3 end-3 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                  <AlertTriangle size={10}/>{isAr ? "معطّلة" : "Disabled"}
                </span>
              )}
              {p.active && (
                <span className="absolute top-3 end-3 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                  <CheckCircle2 size={10}/>{isAr ? "نشطة" : "Active"}
                </span>
              )}
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-400">
                <Crown size={11}/><code className="font-mono">{p.code}</code>
              </div>
              <div className="font-heading text-xl gold-text mt-1">{p.name_en}</div>
              <div className="text-xs text-stone-500">{p.name_ar}</div>
              <div className="text-3xl font-bold text-stone-800 num-display mt-3">
                {p.monthly_price}
                <span className="text-xs text-stone-400 font-normal"> {p.currency || "QAR"}/mo</span>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-4 pb-3 border-b border-stone-100">
                <LimitChip Icon={Building2} value={p.max_branches} label={isAr ? "فروع" : "branches"} lang={lang}/>
                <LimitChip Icon={UsersIcon} value={p.max_users} label={isAr ? "مستخدمين" : "users"} lang={lang}/>
                <LimitChip Icon={Scissors} value={p.max_services} label={isAr ? "خدمات" : "services"} lang={lang}/>
                <LimitChip Icon={Calendar} value={p.max_bookings_per_month} label={isAr ? "حجز/شهر" : "bookings/mo"} lang={lang}/>
              </div>

              {p.features && p.features.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {p.features.slice(0, 6).map(f => (
                    <span key={f} className="bg-[var(--gold-tint)] text-[var(--gold)] text-[10px] px-1.5 py-0.5 rounded-full">{f}</span>
                  ))}
                  {p.features.length > 6 && (
                    <span className="text-[10px] text-stone-400">+{p.features.length - 6}</span>
                  )}
                </div>
              )}

              {p.description && (
                <p className="text-xs text-stone-500 mt-3 leading-relaxed">{p.description}</p>
              )}

              <div className="mt-4 flex items-center justify-between gap-2">
                <span className="text-[11px] text-stone-400">
                  {isAr ? "مستأجرين على هذه الخطة" : "Tenants on plan"}: <span className="text-stone-700 font-semibold num-display">{p.tenants_count ?? 0}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onToggleActive(p)}
                    className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 transition"
                    title={p.active ? (isAr ? "تعطيل" : "Disable") : (isAr ? "تفعيل" : "Activate")}
                    data-testid={`plan-toggle-${p.code}`}
                  >
                    <Power size={14}/>
                  </button>
                  <button
                    onClick={() => setModal({ open: true, mode: "edit", initial: p })}
                    className="p-1.5 rounded-lg hover:bg-[var(--gold-tint)] text-[var(--gold)] transition"
                    title={isAr ? "تعديل" : "Edit"}
                    data-testid={`plan-edit-${p.code}`}
                  >
                    <Edit3 size={14}/>
                  </button>
                  <button
                    onClick={() => onDelete(p)}
                    className="p-1.5 rounded-lg hover:bg-rose-100 text-rose-500 transition"
                    disabled={p.tenants_count > 0}
                    title={p.tenants_count > 0 ? (isAr ? "لا يمكن الحذف — هناك مستأجرين عليها" : "Cannot delete — tenants on this plan") : (isAr ? "حذف" : "Delete")}
                    data-testid={`plan-delete-${p.code}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PlanModal
        open={modal.open}
        mode={modal.mode}
        initial={modal.initial}
        onClose={() => setModal({ open: false, mode: "create", initial: null })}
        onSaved={load}
        lang={lang}
      />
      <ConfirmDialogSlot />
    </div>
  );
}
